import { Box, Button, Chip, Divider, Stack, Typography, useTheme } from "@mui/material";
import { DateTime } from "luxon";
import { useNavigate } from "react-router";
import { AvatarGroup, MeetingNotesDrawerContent, SignalRGroup, useDrawer } from "../../../../components";
import { Links } from "../../../../constants";
import { queryKeys, TeamMeetingDto } from "../../../../http";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTeamPageLayoutContext } from "../..";
import { useSignalR } from "../../../../hooks";

interface IProps {
  meeting: TeamMeetingDto;
  onSettingsClicked?: (meetingGuid: string) => void;
}

const TeamMeetingListItem = (props: IProps) => {
  const { team } = useTeamPageLayoutContext();
  const { showDrawer } = useDrawer();
  const theme = useTheme();
  const navigate = useNavigate();
  const signalR = useSignalR();
  const queryClient = useQueryClient();

  const inProgress = props.meeting.currentInstance != null;
  const lastInstance =
    props.meeting.lastInstanceDateTimeUtc != null
      ? DateTime.fromISO(props.meeting.lastInstanceDateTimeUtc, { zone: "utc" })
          .toLocal()
          .toLocaleString(DateTime.DATE_MED)
      : null;

  useEffect(() => {
    signalR.joinGroup(SignalRGroup.MeetingListItem(props.meeting.guid));
    return () => signalR.leaveGroup(SignalRGroup.MeetingListItem(props.meeting.guid));
  }, [props.meeting.guid, signalR]);

  useEffect(() => {
    const handleShouldRefetchMeetingListItem = () => {
      queryClient.invalidateQueries(queryKeys.meetings.getMeetingsForTeam(team.guid));
    };

    signalR.onShouldRefetchMeetingListItem(handleShouldRefetchMeetingListItem);

    return () => signalR.offShouldRefetchMeetingListItem(handleShouldRefetchMeetingListItem);
  }, [queryClient, signalR, team]);

  function handleMeetingNotesClicked(meetingGuid: string) {
    showDrawer({
      title: (
        <Stack>
          <Typography variant="h6">Meeting Notes</Typography>
          <Typography variant="caption">Meeting Notes are accessible to all meeting participants.</Typography>
        </Stack>
      ),
      content: <MeetingNotesDrawerContent meetingGuid={meetingGuid} />,
    });
  }

  return (
    <>
      <Stack spacing={0.5} sx={{ p: 1, backgroundColor: inProgress ? theme.palette.primary.opacity5 : undefined }}>
        {inProgress && <Chip color="primary" label="In Progress" />}
        <Box>
          <Typography variant="h5">{props.meeting.title}</Typography>
          {props.meeting.description && <Typography>{props.meeting.description}</Typography>}
          <Stack
            direction="row"
            spacing={0.25}
            divider={<Divider flexItem orientation="vertical" sx={{ height: 12, alignSelf: "center" }} />}
            sx={{ alignItems: "center" }}
          >
            <Typography variant="caption">{props.meeting.instancesCount} Occurrences</Typography>
            {lastInstance != null && <Typography variant="caption">Last Meeting {lastInstance}</Typography>}
            {props.meeting.canEdit && (
              <Button
                variant="text"
                size="small"
                sx={{ minWidth: 0 }}
                disableRipple
                disabled={props.meeting.currentInstance != null}
                onClick={() => props.onSettingsClicked?.(props.meeting.guid)}
              >
                Settings
              </Button>
            )}
            {props.meeting.isInvited && (
              <Button
                variant="text"
                size="small"
                disableRipple
                onClick={() => handleMeetingNotesClicked(props.meeting.guid)}
              >
                Meeting Notes
              </Button>
            )}
          </Stack>
        </Box>

        {props.meeting.currentInstance != null && (
          <AvatarGroup users={props.meeting.currentInstance.currentParticipants} max={2} />
        )}

        {props.meeting.isInvited && (
          <Button
            variant="tertiary"
            sx={{ alignSelf: "start" }}
            onClick={() => navigate(Links.Meeting(props.meeting.guid))}
          >
            {inProgress ? "Join Meeting" : "Start Meeting"}
          </Button>
        )}
      </Stack>
    </>
  );
};

export { TeamMeetingListItem };
